<template>
  <div class="report-page">
    <h2>Laporan Keuangan</h2>

    <div class="filter-section">
      <h3>Filter Periode</h3>
      <div class="form-group">
        <label for="start-date">Dari Tanggal:</label>
        <input type="date" id="start-date" v-model="startDate">
      </div>
      <div class="form-group">
        <label for="end-date">Sampai Tanggal:</label>
        <input type="date" id="end-date" v-model="endDate">
      </div>

      <div class="form-group" v-if="isOwner">
        <label for="user-filter">Filter Pengguna:</label>
        <select id="user-filter" v-model="selectedUserId">
          <option value="">Semua Pengguna</option>
          <option v-for="user in users" :key="user.ID" :value="user.ID">{{ user.Username }}</option>
        </select>
      </div>
      <button @click="resetFilter" class="filter-button reset-button">Reset Filter</button>
    </div>

    <hr>

    <div class="summary-cards">
      <div class="card income-card">
        <h3>Total Pemasukan (Periode Ini)</h3>
        <p class="amount">Rp {{ totalIncome.toLocaleString('id-ID') }}</p>
      </div>
      <div class="card expense-card">
        <h3>Total Pengeluaran (Periode Ini)</h3>
        <p class="amount">Rp {{ totalExpense.toLocaleString('id-ID') }}</p>
      </div>
    </div>
    <div class="summary-cards">
      <div class="card initial-balance-card">
        <h3>Saldo Awal (Sebelum {{ formatDisplayDate(startDate) }})</h3>
        <p class="amount">Rp {{ initialBalance.toLocaleString('id-ID') }}</p>
      </div>
      <div :class="['card', 'balance-card', { 'negative-balance': balance < 0 }]">
        <h3>Saldo Akhir (Periode Ini)</h3>
        <p class="amount">Rp {{ balance.toLocaleString('id-ID') }}</p>
      </div>
    </div>

    <hr>

    <h3>Detail Transaksi (Periode Terpilih)</h3>
    <div v-if="filteredTransactions.length > 0">
      <table>
        <thead>
          <tr>
            <th>Tanggal</th>
            <th>Deskripsi</th>
            <th v-if="isOwner">Pengguna</th> <th>Struk</th>
            <th class="text-right">Debet</th>
            <th class="text-right">Kredit</th>
            <th class="text-right">Saldo</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(transaction, index) in filteredTransactions" :key="transaction.id">
            <td>{{ new Date(transaction.date).toLocaleDateString('id-ID') }}</td>
            <td>{{ transaction.description }}</td>
            <td v-if="isOwner">{{ getUsernameById(transaction.userId) }}</td> <td>
              <a v-if="transaction.type === 'expense' && transaction.receiptUrl" :href="getImageUrl(transaction.receiptUrl)" target="_blank">
                <img :src="getImageUrl(transaction.receiptUrl)" alt="Struk" class="receipt-thumbnail">
              </a>
              <span v-else>-</span>
            </td>
            <td class="text-right">
              <span v-if="transaction.type === 'income'">Rp {{ transaction.amount.toLocaleString('id-ID') }}</span>
              <span v-else>-</span>
            </td>
            <td class="text-right">
              <span v-if="transaction.type === 'expense'">Rp {{ transaction.amount.toLocaleString('id-ID') }}</span>
              <span v-else>-</span>
            </td>
            <td class="text-right">Rp {{ getRunningBalance(index).toLocaleString('id-ID') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p>Tidak ada transaksi dalam periode yang dipilih.</p>
    </div>
    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  data() {
    return {
      allTransactions: [],
      users: [],
      startDate: '',
      endDate: '',
      selectedUserId: '',
      error: null,
      runningBalancesCache: [],
      initialBalance: 0,
      currentUserRole: '',
      currentUserId: null,
    };
  },
  computed: {
    isOwner() {
      return this.currentUserRole === 'owner';
    },
    filteredTransactions() {
      const start = this.startDate ? new Date(this.startDate) : null;
      const end = this.endDate ? new Date(this.endDate + 'T23:59:59') : null;

      let filtered = this.allTransactions.filter(t => {
        const transactionDate = new Date(t.date);
        const isWithinDateRange = (!start || transactionDate >= start) && (!end || transactionDate <= end);

        const isSelectedUser = (!this.isOwner || !this.selectedUserId || t.userId === parseInt(this.selectedUserId));
        
        return isWithinDateRange && isSelectedUser;
      });

      const sorted = [...filtered].sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });

      this.calculateRunningBalances(sorted);
      return sorted;
    },
    totalIncome() {
      return this.filteredTransactions.filter(t => t.type === 'income')
                                     .reduce((sum, t) => sum + t.amount, 0);
    },
    totalExpense() {
      return this.filteredTransactions.filter(t => t.type === 'expense')
                                     .reduce((sum, t) => sum + t.amount, 0);
    },
    balance() {
      return this.initialBalance + (this.totalIncome - this.totalExpense);
    }
  },
  methods: {
    getImageUrl(relativePath) {
      return `https://api.pala.linksoftindo.com${relativePath}`;
    },
    getUsernameById(userId) {
      const user = this.users.find(u => u.ID === userId);
      return user ? user.Username : 'N/A';
    },
    async fetchUsers() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.pala.linksoftindo.com/api/users/', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        this.users = response.data;
      } catch (err) {
        console.error('Error fetching users:', err);
        Swal.fire({
          icon: 'error',
          title: 'Gagal Memuat Pengguna!',
          text: 'Tidak dapat mengambil daftar pengguna untuk filter.',
        });
      }
    },
    async fetchAllTransactions() {
      try {
        this.error = null;
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user'));

        if (!token) {
          this.$router.push('/login');
          return;
        }

        this.currentUserRole = user ? user.role : '';
        this.currentUserId = user ? user.ID : null;
        
        var sql = "";
        if (this.isOwner) {
          sql = 'https://api.pala.linksoftindo.com/api/transactions/?type=all';
        } else {
          sql = 'https://api.pala.linksoftindo.com/api/transactions/?type=all&userID=true';
        }
        const response = await axios.get(sql, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        this.allTransactions = response.data;

        if (!this.isOwner && this.currentUserId) {
            this.selectedUserId = this.currentUserId;
        } else if (this.isOwner && !this.selectedUserId) {
            this.selectedUserId = '';
        }

      } catch (err) {
        this.error = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Gagal mengambil data laporan.';
        console.error('Error fetching all transactions:', err);
        Swal.fire({
          icon: 'error',
          title: 'Gagal Memuat Transaksi!',
          text: this.error,
        });
      }
    },
    calculateRunningBalances(transactions) {
      this.runningBalancesCache = [];
      let currentBalance = this.initialBalance;
      transactions.forEach(t => {
        if (t.type === 'income') {
          currentBalance += t.amount;
        } else {
          currentBalance -= t.amount;
        }
        this.runningBalancesCache.push(currentBalance);
      });
    },
    getRunningBalance(index) {
      return this.runningBalancesCache[index] || this.initialBalance;
    },
    calculateInitialBalance() {
      this.initialBalance = 0;
      const start = this.startDate ? new Date(this.startDate) : null;

      let transactionsForInitialBalance = this.allTransactions;

      if (!this.isOwner) {
          transactionsForInitialBalance = this.allTransactions.filter(t => t.userId === this.currentUserId);
      } else if (this.isOwner && this.selectedUserId) {
          transactionsForInitialBalance = this.allTransactions.filter(t => t.userId === parseInt(this.selectedUserId));
      }

      if (start) {
        this.initialBalance = transactionsForInitialBalance.filter(t => {
          const transactionDate = new Date(t.date);
          return transactionDate < start;
        }).reduce((sum, t) => {
          return sum + (t.type === 'income' ? t.amount : -t.amount);
        }, 0);
      } else {
          this.initialBalance = 0;
      }
    },
    resetFilter() {
      this.startDate = '';
      this.endDate = '';
      if (this.isOwner) {
        this.selectedUserId = '';
      } else {
        this.selectedUserId = this.currentUserId;
      }
    },
    formatDisplayDate(dateString) {
      if (!dateString) {
        return 'awal periode';
      }
      return new Date(dateString).toLocaleDateString('id-ID');
    }
  },
  async created() {
    const user = JSON.parse(localStorage.getItem('user'));
    this.currentUserRole = user ? user.role : '';
    this.currentUserId = user ? user.ID : null;

    if (this.isOwner) {
      await this.fetchUsers();
    }
    await this.fetchAllTransactions();
  },
  watch: {
    startDate: {
      handler() {
        this.calculateInitialBalance();
      },
      immediate: true
    },
    endDate: {
      handler() {
      },
      immediate: true
    },
    selectedUserId: {
        handler() {
            this.calculateInitialBalance();
        }
    }
  }
};
</script>

<style scoped>
/* Tambahkan styling baru untuk select */
.filter-section .form-group select {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  width: 200px; /* Sesuaikan lebar sesuai kebutuhan */
}

/* Penyesuaian lain untuk tata letak filter jika diperlukan */
.filter-section .form-group {
    margin-right: 20px;
    margin-bottom: 15px;
    vertical-align: top;
    display: inline-block; /* Agar elemen filter tetap sejajar */
}

/* Existing styles below (no changes needed) */
.report-page {
  max-width: 900px;
  margin: 20px auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

h2 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

/* Filter Section */
.filter-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
  text-align: center;
}

.filter-section h3 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.filter-section .form-group {
  display: inline-block;
  margin-right: 20px;
  margin-bottom: 15px;
  vertical-align: top;
}

.filter-section label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

.filter-section input[type="date"] {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.filter-button { /* Hanya untuk tombol reset sekarang */
  background-color: #6c757d; /* Abu-abu */
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 10px;
  margin-left: 10px;
  transition: background-color 0.3s ease;
}

.filter-button:hover {
  background-color: #5a6268;
}

hr {
  border: 0;
  border-top: 1px solid #eee;
  margin: 30px 0;
}

/* Summary Cards */
.summary-cards {
  display: flex;
  justify-content: space-around;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.card {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  min-width: 200px;
}

.card h3 {
  margin-top: 0;
  color: #555;
  font-size: 1.1em;
}

.card .amount {
  font-size: 2em;
  font-weight: bold;
  margin-top: 10px;
}

/* Specific card styles */
.initial-balance-card {
  background-color: #e0f2f7;
  color: #007bff;
}
.initial-balance-card .amount {
  color: #0056b3;
}

.income-card .amount { color: #4CAF50; }
.expense-card .amount { color: #f44336; }
.balance-card .amount { color: #2196F3; }
.negative-balance .amount { color: #f44336; }


/* Table styles */
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

th, td {
  border: 1px solid #eee;
  padding: 12px 15px;
  text-align: left;
}

th {
  background-color: #f2f2f2;
  font-weight: bold;
  color: #666;
}

.text-right {
  text-align: right;
}

tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

tbody tr:hover {
  background-color: #f1f1f1;
}

.income-type {
  color: #4CAF50;
  font-weight: bold;
}

.expense-type {
  color: #f44336;
  font-weight: bold;
}

.error-message {
  color: #d9534f;
  background-color: #f2dede;
  border: 1px solid #ebccd1;
  padding: 10px;
  border-radius: 4px;
  margin-top: 20px;
  text-align: center;
}

.receipt-thumbnail {
  width: 50px;
  height: auto;
  border-radius: 4px;
  cursor: pointer;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .summary-cards {
    flex-direction: column;
    align-items: center;
  }
  .card {
    width: 90%;
  }
  .filter-section .form-group {
    display: block;
    margin-right: 0;
  }
  .filter-section input[type="date"],
  .filter-section select { /* Tambahkan select untuk media query */
    width: 90%;
  }
  .filter-button {
    width: 90%;
    margin-left: 0;
    margin-top: 15px;
  }
}
</style>